/**
 * BookmarksPanel — slide-in side panel listing the user's bookmarks for
 * the current book. Click any row to jump the reader to that page.
 *
 * Also exposes a `<BookmarkButton>` companion (default export) that
 * lives in the reader toolbar and opens a modal where the user types
 * a short note for the current page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Bookmark, BookmarkPlus, X, Trash2, Loader2, Pencil, Check,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export const BookmarksPanel = ({
  bookId,
  open,
  onClose,
  onJumpToPage,
  refreshKey,  // bump to force re-fetch (e.g. after add)
}) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingNote, setEditingNote] = useState('');
  const { toast } = useToast();

  const fetchList = useCallback(async () => {
    if (!bookId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookmarks/${bookId}`, { headers: authHeader() });
      setList(res.data?.bookmarks || []);
    } catch (e) {
      // Silent — empty list is fine
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList, refreshKey]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/bookmarks/${id}`, { headers: authHeader() });
      setList((cur) => cur.filter((b) => b.id !== id));
      toast({ title: 'Bookmark removed' });
    } catch (e) {
      toast({
        title: 'Could not delete',
        description: e?.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    }
  };

  const handleSaveEdit = async (id) => {
    try {
      const res = await axios.put(
        `${API}/bookmarks/${id}`,
        { note: editingNote },
        { headers: authHeader() },
      );
      setList((cur) => cur.map((b) => (b.id === id ? res.data : b)));
      setEditingId(null);
      toast({ title: 'Bookmark updated' });
    } catch (e) {
      toast({
        title: 'Could not save',
        description: e?.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-y-0 right-0 z-50 w-full sm:w-96 bg-gray-900 border-l border-white/10 shadow-2xl flex flex-col"
      data-testid="bookmarks-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-white">
          <Bookmark className="h-4 w-4 text-amber-400" />
          <h3 className="font-semibold text-sm">Your bookmarks</h3>
          <span
            className="text-xs text-gray-400 tabular-nums"
            data-testid="bookmarks-count"
          >
            ({list.length})
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/5"
          data-testid="bookmarks-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : list.length === 0 ? (
          <p className="text-center text-gray-500 text-sm py-12 px-6">
            No bookmarks yet. Use the bookmark icon in the reader to mark important pages.
          </p>
        ) : (
          list.map((b) => (
            <div
              key={b.id}
              className="rounded-md border border-white/5 bg-white/[0.02] hover:bg-white/5 p-3 transition-colors"
              data-testid={`bookmark-row-${b.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => onJumpToPage(b.page)}
                  className="text-amber-300 hover:text-amber-200 text-xs font-mono font-semibold flex items-center gap-1"
                  data-testid={`bookmark-jump-${b.id}`}
                >
                  <Bookmark className="h-3 w-3" />
                  page {b.page}
                </button>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(b.id);
                      setEditingNote(b.note || '');
                    }}
                    className="p-1 rounded text-gray-400 hover:text-white hover:bg-white/5"
                    data-testid={`bookmark-edit-${b.id}`}
                    title="Edit note"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(b.id)}
                    className="p-1 rounded text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                    data-testid={`bookmark-delete-${b.id}`}
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
              {editingId === b.id ? (
                <div className="mt-2 flex items-end gap-2">
                  <Input
                    value={editingNote}
                    onChange={(e) => setEditingNote(e.target.value)}
                    placeholder="Update note…"
                    className="flex-1 bg-white/5 border-white/10 text-white text-sm h-8"
                    data-testid={`bookmark-edit-input-${b.id}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => handleSaveEdit(b.id)}
                    className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white px-2"
                    data-testid={`bookmark-save-${b.id}`}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                b.note && (
                  <p className="text-sm text-gray-300 mt-1.5 leading-relaxed whitespace-pre-wrap">
                    {b.note}
                  </p>
                )
              )}
              <p className="text-[10px] text-gray-500 mt-2 tabular-nums">
                {new Date(b.created_at).toLocaleString()}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};


/**
 * AddBookmarkDialog — small inline modal triggered from the reader
 * toolbar. Captures a note for the *current* page.
 */
export const AddBookmarkDialog = ({ open, currentPage, bookId, onClose, onCreated }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  // Reset note when dialog opens for a different page
  useEffect(() => {
    if (open) setNote('');
  }, [open, currentPage]);

  if (!open) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/bookmarks`,
        { book_id: bookId, page: currentPage, note },
        { headers: authHeader() },
      );
      toast({
        title: 'Bookmark saved',
        description: `Page ${currentPage}${(note || '').trim() ? ' · ' + note.trim().slice(0, 60) : ''}`,
      });
      onCreated && onCreated(res.data);
      onClose();
    } catch (e) {
      toast({
        title: 'Could not save bookmark',
        description: e?.response?.data?.detail || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      data-testid="add-bookmark-dialog"
    >
      <div className="bg-gray-900 rounded-xl border border-white/10 shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-2 text-white">
            <BookmarkPlus className="h-4 w-4 text-amber-400" />
            <h3 className="font-semibold text-sm">
              Bookmark page <span className="text-amber-300">{currentPage}</span>
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/5"
            data-testid="add-bookmark-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="text-xs text-gray-400 uppercase tracking-wider">Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="e.g. 'where the twist starts' or 'look up ref. p.142'"
            className="w-full rounded-md bg-white/5 border border-white/10 text-white px-3 py-2 text-sm resize-y"
            data-testid="add-bookmark-note"
            autoFocus
          />
          <p className="text-[10px] text-gray-500">
            You can find this bookmark anytime in the bookmarks panel.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/10">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-gray-300 hover:text-white"
            data-testid="add-bookmark-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-amber-950 font-semibold"
            data-testid="add-bookmark-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save bookmark'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookmarksPanel;
